import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ButtonBase, Skeleton, Stack, Typography } from '@mui/material';
import CandleCardHero from '@/features/candleCard/CandleCardHero';
import { candleCardApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';

/**
 * The client's card, on the dashboard, directly under the greeting.
 *
 * It is the REAL card — the same component the section leads with — rather
 * than a tile that describes one. Tapping it opens the section, so the card
 * the person sees first is the card they then hold.
 *
 * It shares the section's query key, so opening the section costs no second
 * fetch and the two can never disagree about which tier someone is on.
 */
export default function DashboardCandleCard() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('fa') ? 'fa' : 'en';
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['candle-card'],
    queryFn: () => candleCardApi.getCandleCard(),
    staleTime: 60000,
  });

  if (isLoading) {
    return <Skeleton variant="rounded"
      sx={{ aspectRatio: '1.586 / 1', maxWidth: 420, mx: 'auto', borderRadius: 4 }} />;
  }

  const tier = data?.tier || 'free';
  const reports = data?.reports || [];
  const holder = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const fmt = (n) => new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB').format(n);

  return (
    <ButtonBase
      onClick={() => navigate('/candle-card')}
      focusRipple
      aria-label={t('dashboard.nav.candleCard.title')}
      sx={{
        display: 'block', width: '100%', borderRadius: 4, textAlign: 'start',
        transition: 'transform .18s ease',
        '&:hover': { transform: 'translateY(-2px)' },
        '&.Mui-focusVisible': { outline: '2px solid #11223E', outlineOffset: 3 },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none', '&:hover': { transform: 'none' },
        },
      }}
    >
      <CandleCardHero
        tier={tier}
        holderName={holder}
        keptUntil={data?.photosKeptUntil}
        tierLabel={t(`candleCard.tier.${tier}`)}
        validLabel={t('candleCard.keptUntil')}
        noCoverLabel={t('candleCard.noCover')}
        formatDate={(iso) => new Date(iso).toLocaleDateString(
          lang === 'fa' ? 'fa-IR' : 'en-GB', { year: 'numeric', month: 'long' })}
      />
      <Stack direction="row" justifyContent="space-between" alignItems="baseline"
        sx={{ maxWidth: 420, mx: 'auto', mt: 1, px: .5, gap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary' }}>
          {t('dashboard.nav.candleCard.title')}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {reports.length
            ? t('candleCard.readingsCount', { n: fmt(reports.length), count: reports.length })
            : t('candleCard.readings.emptyTitle')}
        </Typography>
      </Stack>
    </ButtonBase>
  );
}
